import { customPrizeOption } from "Routes/SetFan/constants/prizes";
import { PromptFormState } from "./defaults/types";
import { SurveyTemplateConfig } from "./types";
import { builderToClass } from "@max/common";
import { generateUUID } from "@max/common/dist/setfan/modules/util";

interface CreateTemplateFields {
  template: SurveyTemplateConfig;
  patch?: SurveyTemplateConfig;
  formState?: PromptFormState;
}

const processSurveyQuestions = (template, patch) => {
  return template.surveyQuestions?.map((sq) => {
    const update = patch.surveyQuestions?.find(({ id }) => id === sq.id) || {};
    const question = { ...sq, required: true, id: generateUUID() };

    if (update.label?.en && typeof update.label.en === "string") {
      question.label = update.label;
    }

    if (
      question.options &&
      update.options &&
      Array.isArray(update.options) &&
      update.options[0]?.label?.en
    ) {
      question.options = update.options.map((opt, optIdx) => ({
        label: {
          en: opt.label?.en || question.options[optIdx]?.label?.en || "",
        },
      }));
    }

    return builderToClass(question).toBuilderConfig();
  });
};

const mergeContent = (
  key: string,
  patchFields: Record<string, any> | undefined,
  templateFields: Record<string, any> | undefined,
  defaultValue: string = "",
): string =>
  // prefer patch value, then template value, then default
  patchFields?.[key]?.content?.en ??
  templateFields?.[key]?.content?.en ??
  defaultValue;

const mergeFields = (
  template: SurveyTemplateConfig,
  patch: Partial<SurveyTemplateConfig> = {},
  formState?: PromptFormState,
) => {
  const templateFields = template.fields ?? {};
  const patchFields = patch.fields ?? {};

  const keys = ["header", "body", "thankYouHeader", "thankYouBody"];
  const fields: Partial<SurveyTemplateConfig["fields"]> = {};

  for (const key of keys) {
    fields[key] = {
      content: {
        en: mergeContent(key, patchFields, templateFields, ""),
      },
    };
  }

  if (formState?.hasSweeps === "Yes") {
    fields.isContest = { content: true };
    fields.hasContestCustomRules = { content: null };
    fields.contestPrize = { content: { en: customPrizeOption.value } };
    fields.contestPrizeValue = {
      content:
        patchFields?.contestPrizeValue?.content ??
        templateFields?.contestPrizeValue?.content ??
        150,
    };
    fields.contestCustomPrizeDescription = {
      content: {
        en: mergeContent(
          "contestCustomPrizeDescription",
          patchFields,
          templateFields,
          formState?.prize || "",
        ),
      },
    };
  }

  return { ...templateFields, ...fields };
};

export const convertTemplateToConfig = ({
  template,
  patch = {},
  formState,
}: CreateTemplateFields): SurveyTemplateConfig => {
  const config = {
    ...template,
    fields: mergeFields(template, patch, formState),
  };

  if (config.surveyQuestions?.length) {
    config.surveyQuestions = processSurveyQuestions(template, patch);
  }

  if (formState?.hasSweeps === "Yes") {
    config.flags = {
      ...config.flags,
      showContestConfirmationMessage: true,
    };
  }

  return config;
};
